import {EndpointBuilder} from '@reduxjs/toolkit/dist/query/endpointDefinitions';
import {IFlight, IPassenger} from '../interfaces/flight';
import {authAxiosInstance} from '../../../utils/auth-axios-instance';

export const type = 'declarations';

// ---------------------------------------------------------------------------
// Shared types for declaration generation
// ---------------------------------------------------------------------------

export interface DeclarationPassenger {
  name: string;
  pnr: string;
  ticketNumber: string;
  documentType: string;
  idNumber: string;
  nif: string;
  boardingTime?: string;
}

export interface DeclarationReason {
  code: string;
  labelEN: string;
  labelPT: string;
  textPT: string;
  textEN: string;
}

interface BaseDeclarationRequest {
  carrier: string;
  flightNumber: string;
  date: string;
  origin: string;
  destination: string;
  scheduledDeparture: string;
  passengers: DeclarationPassenger[];
  recipientEntity: string;
  deliveryMethod: 'email' | 'download';
  emails: string[];
  notes: string;
}

export interface DelayDeclarationRequest extends BaseDeclarationRequest {
  estimatedDeparture: string;
  actualDeparture: string;
  scheduledArrival: string;
  estimatedArrival: string;
  actualArrival: string;
  totalDelayMinutes: number;
  reasons: DeclarationReason[];
}

export interface BoardingDeclarationRequest extends BaseDeclarationRequest {
  scheduledArrival: string;
  actualDeparture: string;
  actualArrival: string;
}

export interface CancellationDeclarationRequest extends BaseDeclarationRequest {
  reasons: DeclarationReason[];
}

export type DeclarationResponse = Blob | {message: string};

export interface TicketCoupon {
  couponStatus: string;
  flightNumber: string;
  carrier: string;
  originAirport: string;
  destinationAirport: string;
  date: string;
  std?: string;
  flownFlightNumber: string;
  flownCarrier: string;
  flownOriginAirport: string;
  flownDestinationAirport: string;
  flownDate: string;
  flownStd?: string;
  newTicketNumber?: string;
}

export interface TicketDocumentInfo {
  passenger: {firstName: string; lastName: string};
  reservationCode: string;
  coupons: TicketCoupon[];
}

export interface TicketEntry {
  company: string;
  number: string;
  documentType: string;
  idNumber: string;
  nif: string;
  documentInfo: TicketDocumentInfo;
}

export interface TicketUsageDeclarationRequest {
  tickets: TicketEntry[];
  deliveryMethod: 'email' | 'download';
  emails: string[];
}

export interface ScheduleTicketUsageDeclarationRequest {
  tickets: TicketEntry[];
  emails: string[];
}

export interface ScheduledDeclarationResult {
  ref: string;
  scheduledFor: string;
}

export interface ScheduledDeclarationInfo {
  ref: string;
  status: 'pending' | 'processing' | 'sent';
  emails: string[];
  scheduledFor: string;
  lastCheckedAt: string | null;
  completedAt: string | null;
}

export const declarationsEndpoints = (builder: EndpointBuilder<any, any, any>) => ({
  getFlights: builder.mutation<IFlight[], {carrier: string; flightNumber: string; date: string; origin: string}>({
    queryFn: async ({carrier, flightNumber, date, origin}) => {
      try {
        const result = await authAxiosInstance({
          url: `/declarations/flights/${carrier}/${flightNumber}/${date}/${origin}`,
          method: 'GET'
        });
        return {data: result.data};
      } catch (e: any) {
        return {
          error: {
            status: e?.response?.status ?? 'FETCH_ERROR',
            error: e?.response?.data?.description ?? e?.message ?? 'Unknown error'
          }
        };
      }
    }
  }),

  getFlight: builder.mutation<IFlight, {carrier: string; flightNumber: string; date: string; origin: string}>({
    queryFn: async ({carrier, flightNumber, date, origin}) => {
      try {
        const result = await authAxiosInstance({
          url: `/declarations/flight/${carrier}/${flightNumber}/${date}/${origin}`,
          method: 'GET'
        });
        return {data: result.data};
      } catch (e: any) {
        return {
          error: {
            status: e?.response?.status ?? 'FETCH_ERROR',
            error: e?.response?.data?.description ?? e?.message ?? 'Unknown error'
          }
        };
      }
    }
  }),

  getFlownPax: builder.mutation<IPassenger[], {carrier: string; flightNumber: string; date: string; origin: string}>({
    queryFn: async ({carrier, flightNumber, date, origin}) => {
      try {
        const result = await authAxiosInstance({
          url: `/declarations/flight/${carrier}/${flightNumber}/${date}/${origin}/passengers`,
          method: 'GET'
        });
        return {data: result.data};
      } catch (e: any) {
        return {
          error: {
            status: e?.response?.status ?? 'FETCH_ERROR',
            error: e?.response?.data?.description ?? e?.message ?? 'Unknown error'
          }
        };
      }
    }
  }),

  getTicketInfo: builder.mutation<any, {company: string; number: string}>({
    queryFn: async ({company, number}) => {
      try {
        const result = await authAxiosInstance({
          url: `/declarations/ticket/${company}/${number}`,
          method: 'GET'
        });
        return {data: result.data};
      } catch (e: any) {
        return {
          error: {
            status: e?.response?.status ?? 'FETCH_ERROR',
            error: e?.response?.data?.description ?? e?.message ?? 'Unknown error'
          }
        };
      }
    }
  }),

  generateDelayDeclaration: builder.mutation<DeclarationResponse, DelayDeclarationRequest>({
    queryFn: async (payload) => {
      try {
        const result = await authAxiosInstance({
          url: '/declarations/generate/delay',
          method: 'POST',
          data: payload,
          responseType: payload.deliveryMethod === 'download' ? 'blob' : 'json'
        });
        return {data: result.data};
      } catch (e: any) {
        return {
          error: {
            status: e?.response?.status ?? 'FETCH_ERROR',
            error: e?.response?.data?.description ?? e?.message ?? 'Unknown error'
          }
        };
      }
    }
  }),

  generateBoardingDeclaration: builder.mutation<DeclarationResponse, BoardingDeclarationRequest>({
    queryFn: async (payload) => {
      try {
        const result = await authAxiosInstance({
          url: '/declarations/generate/boarding',
          method: 'POST',
          data: payload,
          responseType: payload.deliveryMethod === 'download' ? 'blob' : 'json'
        });
        return {data: result.data};
      } catch (e: any) {
        return {
          error: {
            status: e?.response?.status ?? 'FETCH_ERROR',
            error: e?.response?.data?.description ?? e?.message ?? 'Unknown error'
          }
        };
      }
    }
  }),

  generateCancellationDeclaration: builder.mutation<DeclarationResponse, CancellationDeclarationRequest>({
    queryFn: async (payload) => {
      try {
        const result = await authAxiosInstance({
          url: '/declarations/generate/cancellation',
          method: 'POST',
          data: payload,
          responseType: payload.deliveryMethod === 'download' ? 'blob' : 'json'
        });
        return {data: result.data};
      } catch (e: any) {
        return {
          error: {
            status: e?.response?.status ?? 'FETCH_ERROR',
            error: e?.response?.data?.description ?? e?.message ?? 'Unknown error'
          }
        };
      }
    }
  }),

  generateTicketUsageDeclaration: builder.mutation<DeclarationResponse, TicketUsageDeclarationRequest>({
    queryFn: async (payload) => {
      try {
        const result = await authAxiosInstance({
          url: '/declarations/generate/ticket-usage',
          method: 'POST',
          data: payload,
          responseType: payload.deliveryMethod === 'download' ? 'blob' : 'json'
        });
        return {data: result.data};
      } catch (e: any) {
        return {
          error: {
            status: e?.response?.status ?? 'FETCH_ERROR',
            error: e?.response?.data?.description ?? e?.message ?? 'Unknown error'
          }
        };
      }
    }
  }),

  scheduleTicketUsageDeclaration: builder.mutation<ScheduledDeclarationResult[], ScheduleTicketUsageDeclarationRequest>({
    queryFn: async (payload) => {
      try {
        const result = await authAxiosInstance({
          url: '/declarations/schedule/ticket-usage',
          method: 'POST',
          data: payload
        });
        return {data: result.data};
      } catch (e: any) {
        return {
          error: {
            status: e?.response?.status ?? 'FETCH_ERROR',
            error: e?.response?.data?.description ?? e?.message ?? 'Unknown error'
          }
        };
      }
    }
  }),

  getScheduledTicketUsageDeclarations: builder.mutation<ScheduledDeclarationInfo[], {company: string; number: string}>({
    queryFn: async ({company, number}) => {
      try {
        const result = await authAxiosInstance({
          url: `/declarations/schedule/ticket-usage/${company}/${number}`,
          method: 'GET'
        });
        return {data: result.data};
      } catch (e: any) {
        return {
          error: {
            status: e?.response?.status ?? 'FETCH_ERROR',
            error: e?.response?.data?.description ?? e?.message ?? 'Unknown error'
          }
        };
      }
    }
  }),
});

export default declarationsEndpoints;
